import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Fab, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import starsBackground from 'assets/rewards/stars.svg';
import { IconButtonAnimate } from 'components/animate';
import { APP_BAR_DESKTOP, APP_BAR_MOBILE } from 'config';

import { onMessageListener, requestPermissionAndGetMessagingToken } from 'external/firebase';

import useAuth from 'hooks/useAuth';

import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths';

import DashboardNavbar from './navbar/DashboardNavbar';
import DashboardSidebar from './navbar/DashboardSidebar';

// ----------------------------------------------------------------------

const RootStyle = styled('div')({
    display: 'flex',
    minHeight: '100%',
    overflow: 'hidden',
});

const MainStyle = styled('div')(({ theme }) => ({
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100%',
    paddingTop: APP_BAR_MOBILE + 24,
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.up('lg')]: {
        paddingTop: APP_BAR_DESKTOP,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
}));

const FooterStyle = styled('div')(({ theme }) => ({
    height: 180,
    width: '100%',
    position: 'fixed',
    bottom: 0,
    left: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    backgroundImage:
        'url(data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%221440%22%20height%3D%22382%22%20viewBox%3D%220%200%201440%20382%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22%23FED063%22%20fill-rule%3D%22evenodd%22%20d%3D%22M1440%200c-89%2043.14-185.333%2068.796-289%2076.969C995.5%2089.227%20915.348%2045.21%20694.174%2045.21%20473%2045.21%20362.773%20128.127%20245%20128.127c-78.515%200-160.182-17.053-245-51.158V690h1440V0z%22/%3E%0A%3C/svg%3E%0A)',
    backgroundRepeat: 'no-repeat',
    zIndex: -1,
}));

// ----------------------------------------------------------------------

const DashboardLayout: React.FunctionComponent = () => {
    const theme = useTheme();
    const { profile } = useAuth();
    const { pathname } = useLocation();
    const isRewardsPage =
        pathname.includes(PATH_DASHBOARD.studies.points) ||
        pathname.includes(PATH_DASHBOARD.points.root) ||
        pathname.includes(PATH_DASHBOARD.teacherPrizes.root);

    const [open, setOpen] = useState(false);
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    onMessageListener()
        .then(payload => {
            // @ts-ignore
            enqueueSnackbar(payload.notification.body, {
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
                // @ts-ignore
                title: payload.notification.title,
                // @ts-ignore
                image: payload.notification.image,
                variant: 'notification',
                persist: true,
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        })
        .catch(err => console.log(err));

    useEffect(() => {
        requestPermissionAndGetMessagingToken();
    }, []);

    return (
        <RootStyle
            sx={
                isRewardsPage
                    ? {
                          width: '100%',
                          minHeight: '100vh',
                          backgroundImage: `url(${starsBackground}), linear-gradient(to bottom right, #110457, #0F0545)`,
                          backgroundRepeat: 'no-repeat, repeat',
                          position: 'relative',
                          overflow: 'hidden',
                      }
                    : {}
            }
        >
            {isTablet && <DashboardNavbar onOpenSidebar={() => setOpen(true)} />}
            <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
            <MainStyle
                sx={{
                    transition: theme.transitions.create('margin', {
                        duration: theme.transitions.duration.complex,
                    }),
                }}
            >
                <Outlet />
                {/* {profile?.role === 'parent' && (
                    <Fab
                        color='primary'
                        aria-label='ask-question'
                        id='help-button'
                        size='medium'
                        style={{
                            position: 'fixed',
                            bottom: '16px',
                            right: '16px',
                        }}
                    >
                        <HelpOutlineIcon />
                    </Fab>
                )} */}

                {!isRewardsPage && <FooterStyle />}
            </MainStyle>
        </RootStyle>
    );
};

export default DashboardLayout;
